import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef } from 'react';

// Accessibility Context
const AccessibilityContext = createContext();

// Action types for accessibility state management
const ACCESSIBILITY_ACTIONS = {
    SET_SCREEN_READER_MODE: 'SET_SCREEN_READER_MODE',
    SET_HIGH_CONTRAST: 'SET_HIGH_CONTRAST',
    SET_REDUCED_MOTION: 'SET_REDUCED_MOTION',
    SET_FONT_SIZE: 'SET_FONT_SIZE',
    ANNOUNCE: 'ANNOUNCE',
    SET_FOCUS_TRAP: 'SET_FOCUS_TRAP',
    SET_SKIP_LINKS: 'SET_SKIP_LINKS',
    UPDATE_LANDMARKS: 'UPDATE_LANDMARKS'
};

// Initial accessibility state
const initialState = {
    screenReaderMode: false,
    highContrast: false,
    reducedMotion: false,
    fontSize: 'medium', // small, medium, large, extra-large
    announcements: [],
    focusTrap: null,
    skipLinks: [],
    landmarks: [],
    keyboardNavigation: true
};

// Accessibility reducer
function accessibilityReducer(state, action) {
    switch (action.type) {
        case ACCESSIBILITY_ACTIONS.SET_SCREEN_READER_MODE:
            return { ...state, screenReaderMode: action.payload };

        case ACCESSIBILITY_ACTIONS.SET_HIGH_CONTRAST:
            return { ...state, highContrast: action.payload };

        case ACCESSIBILITY_ACTIONS.SET_REDUCED_MOTION:
            return { ...state, reducedMotion: action.payload };

        case ACCESSIBILITY_ACTIONS.SET_FONT_SIZE:
            return { ...state, fontSize: action.payload };

        case ACCESSIBILITY_ACTIONS.ANNOUNCE:
            return {
                ...state,
                announcements: [...state.announcements, {
                    id: Date.now(),
                    message: action.payload.message,
                    priority: action.payload.priority || 'polite',
                    timestamp: new Date()
                }]
            };

        case ACCESSIBILITY_ACTIONS.SET_FOCUS_TRAP:
            return { ...state, focusTrap: action.payload };

        case ACCESSIBILITY_ACTIONS.SET_SKIP_LINKS:
            return { ...state, skipLinks: action.payload };

        case ACCESSIBILITY_ACTIONS.UPDATE_LANDMARKS:
            return { ...state, landmarks: action.payload };

        default:
            return state;
    }
}

// Accessibility Provider Component
export function AccessibilityProvider({ children }) {
    const [state, dispatch] = useReducer(accessibilityReducer, initialState);
    const liveRegionRef = useRef(null);

    // Load accessibility preferences from localStorage
    useEffect(() => {
        const savedPreferences = localStorage.getItem('accessibility-preferences');
        if (savedPreferences) {
            try {
                const preferences = JSON.parse(savedPreferences);
                Object.entries(preferences).forEach(([key, value]) => {
                    switch (key) {
                        case 'screenReaderMode':
                            dispatch({ type: ACCESSIBILITY_ACTIONS.SET_SCREEN_READER_MODE, payload: value });
                            break;
                        case 'highContrast':
                            dispatch({ type: ACCESSIBILITY_ACTIONS.SET_HIGH_CONTRAST, payload: value });
                            break;
                        case 'reducedMotion':
                            dispatch({ type: ACCESSIBILITY_ACTIONS.SET_REDUCED_MOTION, payload: value });
                            break;
                        case 'fontSize':
                            dispatch({ type: ACCESSIBILITY_ACTIONS.SET_FONT_SIZE, payload: value });
                            break;
                        default:
                            break;
                    }
                });
            } catch (error) {
                console.warn('Failed to load accessibility preferences:', error);
            }
        }
    }, []);

    // Detect system preferences
    useEffect(() => {
        // Detect prefers-reduced-motion
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        if (mediaQuery && mediaQuery.matches) {
            dispatch({ type: ACCESSIBILITY_ACTIONS.SET_REDUCED_MOTION, payload: true });
        }

        const handleChange = (e) => {
            dispatch({ type: ACCESSIBILITY_ACTIONS.SET_REDUCED_MOTION, payload: e.matches });
        };

        if (mediaQuery && mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleChange);
            return () => mediaQuery.removeEventListener('change', handleChange);
        } else if (mediaQuery && mediaQuery.addListener) {
            // Fallback for jsdom / older APIs
            mediaQuery.addListener(handleChange);
            return () => mediaQuery.removeListener(handleChange);
        }
        return () => { };
    }, []);

    // Apply accessibility settings to document
    useEffect(() => {
        const root = document.documentElement;

        // Apply high contrast
        if (state.highContrast) {
            root.classList.add('high-contrast');
        } else {
            root.classList.remove('high-contrast');
        }

        // Apply reduced motion
        if (state.reducedMotion) {
            root.classList.add('reduced-motion');
        } else {
            root.classList.remove('reduced-motion');
        }

        // Apply font size
        root.classList.remove('font-small', 'font-medium', 'font-large', 'font-extra-large');
        root.classList.add(`font-${state.fontSize}`);

        // Apply screen reader mode
        if (state.screenReaderMode) {
            root.classList.add('screen-reader-mode');
        } else {
            root.classList.remove('screen-reader-mode');
        }
    }, [state.highContrast, state.reducedMotion, state.fontSize, state.screenReaderMode]);

    // Save preferences to localStorage
    useEffect(() => {
        const preferences = {
            screenReaderMode: state.screenReaderMode,
            highContrast: state.highContrast,
            reducedMotion: state.reducedMotion,
            fontSize: state.fontSize
        };
        localStorage.setItem('accessibility-preferences', JSON.stringify(preferences));
    }, [state.screenReaderMode, state.highContrast, state.reducedMotion, state.fontSize]);

    // Handle live region announcements
    useEffect(() => {
        if (state.announcements.length > 0 && liveRegionRef.current) {
            const latestAnnouncement = state.announcements[state.announcements.length - 1];
            liveRegionRef.current.textContent = latestAnnouncement.message;

            // Clear old announcements after 5 seconds
            const timer = setTimeout(() => {
                dispatch({
                    type: ACCESSIBILITY_ACTIONS.ANNOUNCE,
                    payload: { message: '', priority: 'polite' }
                });
            }, 5000);

            return () => clearTimeout(timer);
        }
    }, [state.announcements]);

    // Accessibility action creators
    const setScreenReaderMode = useCallback((enabled) => {
        dispatch({ type: ACCESSIBILITY_ACTIONS.SET_SCREEN_READER_MODE, payload: enabled });
    }, []);

    const setHighContrast = useCallback((enabled) => {
        dispatch({ type: ACCESSIBILITY_ACTIONS.SET_HIGH_CONTRAST, payload: enabled });
    }, []);

    const setReducedMotion = useCallback((enabled) => {
        dispatch({ type: ACCESSIBILITY_ACTIONS.SET_REDUCED_MOTION, payload: enabled });
    }, []);

    const setFontSize = useCallback((size) => {
        dispatch({ type: ACCESSIBILITY_ACTIONS.SET_FONT_SIZE, payload: size });
    }, []);

    const announce = useCallback((message, priority = 'polite') => {
        dispatch({
            type: ACCESSIBILITY_ACTIONS.ANNOUNCE,
            payload: { message, priority }
        });
    }, []);

    const setFocusTrap = useCallback((element) => {
        dispatch({ type: ACCESSIBILITY_ACTIONS.SET_FOCUS_TRAP, payload: element });
    }, []);

    const setSkipLinks = useCallback((links) => {
        dispatch({ type: ACCESSIBILITY_ACTIONS.SET_SKIP_LINKS, payload: links });
    }, []);

    const updateLandmarks = useCallback((landmarks) => {
        dispatch({ type: ACCESSIBILITY_ACTIONS.UPDATE_LANDMARKS, payload: landmarks });
    }, []);

    // Focus management utilities
    const focusManagement = {
        // Focus the first focusable element in a container
        focusFirst: (container) => {
            const focusableElements = container.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            if (focusableElements.length > 0) {
                focusableElements[0].focus();
            }
        },

        // Focus the last focusable element in a container
        focusLast: (container) => {
            const focusableElements = container.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            if (focusableElements.length > 0) {
                focusableElements[focusableElements.length - 1].focus();
            }
        },

        // Trap focus within a container
        trapFocus: (container) => {
            const focusableElements = container.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );

            if (focusableElements.length === 0) return;

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            const handleKeyDown = (e) => {
                if (e.key === 'Tab') {
                    if (e.shiftKey) {
                        if (document.activeElement === firstElement) {
                            e.preventDefault();
                            lastElement.focus();
                        }
                    } else {
                        if (document.activeElement === lastElement) {
                            e.preventDefault();
                            firstElement.focus();
                        }
                    }
                }
            };

            container.addEventListener('keydown', handleKeyDown);
            firstElement.focus();

            return () => {
                container.removeEventListener('keydown', handleKeyDown);
            };
        }
    };

    // Keyboard navigation utilities
    const keyboardNavigation = {
        // Handle arrow key navigation in lists
        handleArrowNavigation: (e, items, currentIndex, onIndexChange) => {
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
                    onIndexChange(nextIndex);
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
                    onIndexChange(prevIndex);
                    break;
                case 'Home':
                    e.preventDefault();
                    onIndexChange(0);
                    break;
                case 'End':
                    e.preventDefault();
                    onIndexChange(items.length - 1);
                    break;
                default:
                    break;
            }
        },

        // Handle escape key to close modals/dropdowns
        handleEscape: (callback) => {
            const handleKeyDown = (e) => {
                if (e.key === 'Escape') {
                    callback();
                }
            };

            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    };

    const contextValue = {
        // State
        ...state,

        // Actions
        setScreenReaderMode,
        setHighContrast,
        setReducedMotion,
        setFontSize,
        announce,
        setFocusTrap,
        setSkipLinks,
        updateLandmarks,

        // Utilities
        focusManagement,
        keyboardNavigation
    };

    return (
        <AccessibilityContext.Provider value={contextValue}>
            {children}

            {/* Live region for screen reader announcements */}
            <div
                ref={liveRegionRef}
                aria-live="polite"
                aria-atomic="true"
                className="sr-only"
                role="status"
            />

            {/* Assertive live region for urgent announcements */}
            <div
                aria-live="assertive"
                aria-atomic="true"
                className="sr-only"
                role="alert"
            />

            {/* Skip links */}
            {state.skipLinks.length > 0 && (
                <div className="skip-links">
                    {state.skipLinks.map((link, index) => (
                        <a
                            key={index}
                            href={link.href}
                            className="skip-link"
                            onFocus={(e) => e.target.classList.add('focused')}
                            onBlur={(e) => e.target.classList.remove('focused')}
                        >
                            {link.text}
                        </a>
                    ))}
                </div>
            )}
        </AccessibilityContext.Provider>
    );
}

// Custom hook to use accessibility context
export function useAccessibility() {
    const context = useContext(AccessibilityContext);
    if (!context) {
        throw new Error('useAccessibility must be used within an AccessibilityProvider');
    }
    return context;
}

// HOC for adding accessibility features to components
export function withAccessibility(Component) {
    return function AccessibleComponent(props) {
        const accessibility = useAccessibility();
        return <Component {...props} accessibility={accessibility} />;
    };
}

export default AccessibilityProvider; 